/**
 * @fileoverview Hook de acesso às Bases de Contatos (upload, mapeamento,
 * processamento assíncrono, listagem). Escuta o socket para atualizar status/
 * contadores em tempo real (evento company-<id>-contactBaseStatus).
 */

import { useState, useEffect, useCallback, useRef } from "react";
import api from "../../../services/api";
import toastError from "../../../errors/toastError";
import openSocket from "../../../services/socket-io";
import { canNow } from "../../../hooks/usePermission";

export default function useContactBases(opts) {
  const autoLoad = !opts || opts.autoLoad !== false;
  const [bases, setBases] = useState([]);
  const [loading, setLoading] = useState(false);
  const mountedRef = useRef(true);

  const list = useCallback(async (params = {}) => {
    // Sem ver as bases (audiences.view, com a regra nova ligada), nem pede: o
    // seletor do disparo fica vazio em vez de um aviso de erro.
    if (!canNow("audiences.view", true)) {
      if (mountedRef.current) setBases([]);
      return [];
    }
    setLoading(true);
    try {
      const { data } = await api.get("/contact-bases", { params });
      if (mountedRef.current) setBases(data.bases || []);
      return data.bases || [];
    } catch (err) {
      toastError(err);
      return [];
    } finally {
      if (mountedRef.current) setLoading(false);
    }
  }, []);

  const refresh = useCallback((params) => list(params), [list]);

  const upload = useCallback(async (files) => {
    const form = new FormData();
    Array.from(files).forEach((f) => form.append("files", f));
    const { data } = await api.post("/contact-bases/upload", form, {
      headers: { "Content-Type": "multipart/form-data" },
    });
    return data.bases || [];
  }, []);

  const getPreview = useCallback(async (id) => {
    const { data } = await api.get(`/contact-bases/${id}/preview`);
    return data;
  }, []);

  const confirm = useCallback(async (id, payload) => {
    const { data } = await api.post(`/contact-bases/${id}/confirm`, payload);
    return data;
  }, []);

  const rename = useCallback(async (id, name) => {
    const { data } = await api.put(`/contact-bases/${id}`, { name });
    return data;
  }, []);

  const archive = useCallback(async (id) => {
    const { data } = await api.post(`/contact-bases/${id}/archive`);
    return data;
  }, []);

  const remove = useCallback(async (id) => {
    await api.delete(`/contact-bases/${id}`);
  }, []);

  const getErrorReportUrl = useCallback(async (id) => {
    const { data } = await api.get(`/contact-bases/${id}/error-report`);
    return data.url;
  }, []);

  // Linhas inválidas/duplicadas (com motivo), paginadas — alimenta o modal de
  // relatório de erros. Usa a API autenticada (não o link /public do xlsx).
  const getErrorRows = useCallback(async (id, page = 1) => {
    const { data } = await api.get(`/contact-bases/${id}/rows`, {
      params: { pageNumber: page, onlyInvalid: true },
    });
    return data; // { count, hasMore, rows }
  }, []);

  useEffect(() => {
    mountedRef.current = true;
    const companyId = localStorage.getItem("companyId");
    if (!companyId) return undefined;
    // O socket é um singleton compartilhado por company. Fechamos sobre o `handler`
    // LOCAL no cleanup (em vez de guardá-lo no socket) para remover exatamente o
    // handler deste componente — senão o unmount de uma instância removeria o
    // handler de outra que ainda está montada.
    const socket = openSocket();
    const evt = `company-${companyId}-contactBaseStatus`;
    const handler = (payload) => {
      if (!mountedRef.current) return;
      if (!payload || payload.action !== "update" || !payload.contactBase) return;
      const cb = payload.contactBase;
      setBases((prev) => prev.map((b) => (b.id === cb.id ? { ...b, ...cb } : b)));
    };
    socket.on(evt, handler);
    return () => {
      mountedRef.current = false;
      socket.off(evt, handler);
    };
  }, []);

  useEffect(() => {
    if (autoLoad) list();
  }, [autoLoad, list]);

  return {
    bases,
    setBases,
    loading,
    list,
    refresh,
    upload,
    getPreview,
    confirm,
    rename,
    archive,
    remove,
    getErrorReportUrl,
    getErrorRows,
  };
}
